import React, { useEffect, useMemo, useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../../components/Popup/Popup";
import RequiredLabel from "../../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../../contexts/SessionContext";
import { LanguageEnum } from "../../../../models/Language";
import { ProjectSubjectStatusEnum } from "../../../../models/ProjectSubjects";
import translate from "../../../../services/Translate";
import { fetchCursus, fetchPrograms, updateProjectSubject } from "../../ProjectSubject.function";
import { EditProjectSubjectModalProps } from "./EditProjectSubjectModal.d";

const EditProjectSubjectModal = ({ show, onHide, subject, onUpdated }: EditProjectSubjectModalProps) => {
  const { language } = useSession();

  const [form, setForm] = useState({
    name: subject.name,
    short_name: subject.short_name,
    description: subject.description,
    language: subject.language,
    difficulty: subject.difficulty,
    cursus_id: subject.cursus_id,
    status: subject.status,
    program_id: subject.program?.id ?? null,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [cursus, setCursus] = useState<any[]>([]);
  const [cursusLoaded, setCursusLoaded] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCursus(setCursus, setCursusLoaded, language);
      fetchPrograms(setPrograms, setProgramsLoaded, language);
    }
  }, [show, language]);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _onSave = () => {
    setIsSaving(true);
    updateProjectSubject(
      subject.id,
      form,
      () => {
        onUpdated();
        onHide();
      },
      language,
      () => setIsSaving(false)
    );
  };

  const _selectedCursus = useMemo(() => {
    if (!form.cursus_id) return null;
    return cursus.find((cursus) => cursus.id === form.cursus_id) ?? null;
  }, [form.cursus_id, cursus]);

  const _isValid = useMemo(() => {
    return form.name.trim().length > 0 && form.short_name.trim().length > 0 && !!form.language && !!form.status;
  }, [form]);

  return (
    <Popup
      show={show}
      onClose={() => onHide()}
      size="lg"
      modalTitle={translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.TITLE")}
      footerContent={
        <React.Fragment>
          <DstButton variant="secondary" value={translate(language, "GLOBAL.CANCEL")} clickFunction={() => onHide()} />
          <DstButton
            value={translate(language, "GLOBAL.SAVE")}
            disabled={isSaving || !_isValid}
            loading={isSaving}
            clickFunction={_onSave}
          />
        </React.Fragment>
      }
    >
      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.NAME")} />
        </Form.Label>
        <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="d-flex align-items-center gap-2">
          <RequiredLabel label={translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SHORT_NAME")} />
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="tooltip-short-name">
                {translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SHORT_NAME_TOOLTIP")}
              </Tooltip>
            }
          >
            <span className="dst-icon-info-circle clickable" />
          </OverlayTrigger>
        </Form.Label>

        <Form.Control value={form.short_name} onChange={(event) => _handleChange("short_name", event.target.value)} />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "GLOBAL.DESCRIPTION")}</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={form.description}
          onChange={(event) => _handleChange("description", event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.LANGUAGE")} />
        </Form.Label>
        <Form.Select
          value={form.language}
          onChange={(event) => _handleChange("language", event.target.value as LanguageEnum)}
        >
          <option value="" disabled>
            {translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SELECT")}
          </option>
          {Object.values(LanguageEnum).map((lang) => (
            <option key={lang} value={lang}>
              {translate(language, `PAGES.PROJECT_SUBJECT.EDIT.MAIN.LANGUAGES.${lang}`)}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          <RequiredLabel label={translate(language, "GLOBAL.STATUS")} />
        </Form.Label>
        <Form.Select
          value={form.status}
          onChange={(event) => _handleChange("status", event.target.value as ProjectSubjectStatusEnum)}
        >
          <option value="" disabled>
            {translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SELECT")}
          </option>
          {Object.values(ProjectSubjectStatusEnum).map((status) => (
            <option key={status} value={status.toLowerCase()}>
              {translate(language, `PAGES.PROJECT_SUBJECT.EDIT.MAIN.STATUS_TYPES.${status}`)}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.DIFFICULTY")}</Form.Label>
        <Form.Control
          type="number"
          min={1}
          max={10}
          step={1}
          value={form.difficulty ?? ""}
          onChange={(event) => {
            if (event.target.value === "") {
              _handleChange("difficulty", null);
              return;
            }

            if (Number(event.target.value) >= 1 && Number(event.target.value) <= 10) {
              _handleChange("difficulty", Number(event.target.value));
            }
          }}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.CURSUS")}</Form.Label>
        <MultiSelectDropdown
          btnWidth="100%"
          items={cursus}
          mock={!cursusLoaded}
          isSingleSelect
          badgeItemProperty="name"
          language={language}
          label={_selectedCursus ? _selectedCursus.name : translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SELECT")}
          displayItem={(item: any) => item.name}
          onConfirm={(selected: any[]) => _handleChange("cursus_id", selected.length ? selected[0].id : null)}
          toggleClass="w-100"
          borderToggle
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.PROGRAM")}</Form.Label>
        <MultiSelectDropdown
          btnWidth="100%"
          items={programs}
          mock={!programsLoaded}
          isSingleSelect
          badgeItemProperty="name"
          language={language}
          label={
            form.program_id
              ? programs.find((program) => program.id === form.program_id)?.name
              : translate(language, "PAGES.PROJECT_SUBJECT.EDIT.MAIN.SELECT")
          }
          displayItem={(item: any) => item.name}
          onConfirm={(selected: any[]) => _handleChange("program_id", selected.length ? selected[0].id : null)}
          toggleClass="w-100"
          borderToggle
        />
      </Form.Group>
    </Popup>
  );
};

export default EditProjectSubjectModal;
